import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Sparkles, Save, Loader2, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useModels } from '@/hooks/useContent';
import { usePreferences, useSavePreferences, type UserPreferences } from '@/hooks/usePreferences';
import { ApiError } from '@/lib/api-client';
import { cn } from '@/lib/utils';
import { ProviderModelSelect } from '@/components/ProviderModelSelect';

const FAMILY_LABELS: Record<string, string> = {
  openai: 'OpenAI',
  anthropic: 'Anthropic Claude',
  gemini: 'Google Gemini',
  xai: 'xAI Grok',
  deepseek: 'DeepSeek',
  meta: 'Meta Llama',
  mistral: 'Mistral',
  qwen: 'Qwen (Alibaba)',
  minimax: 'MiniMax',
  glm: 'Zhipu GLM',
  kimi: 'Moonshot Kimi',
  ernie: 'Baidu ERNIE',
  perplexity: 'Perplexity Sonar',
  cohere: 'Cohere',
  nvidia: 'NVIDIA',
  other: 'Diğer',
};

const FAMILY_ORDER = [
  'openai', 'anthropic', 'gemini', 'xai', 'deepseek', 'meta', 'mistral',
  'qwen', 'minimax', 'glm', 'kimi', 'ernie', 'perplexity', 'cohere', 'nvidia', 'other',
];

const TONES = [
  'Profesyonel',
  'Samimi / Dostane',
  'Bilgilendirici',
  'İkna Edici',
  'Eğlenceli',
  'Hikaye Anlatıcı',
  'Resmi',
  'Günlük Konuşma Dili',
  'Motive Edici',
  'Eleştirel',
  'Pazarlama (İkna Edici ve Emin)',
  'Haber Formatı (Genel Haber)',
];

const AUDIENCES = [
  'Genel okuyucu',
  'Gençler',
  'Yetişkinler',
  'Öğrenciler',
  'Girişimciler',
  'Yöneticiler',
  'Teknoloji meraklıları',
  'Ev hanımları',
  'Sporcular',
  'Ebeveynler',
];

const LANGUAGES = ['Türkçe', 'İngilizce', 'Almanca', 'Fransızca', 'İspanyolca', 'Arapça'];

export function PreferencesPage() {
  const { data, isPending } = usePreferences();
  const models = useModels();
  const save = useSavePreferences();

  const [prefs, setPrefs] = useState<UserPreferences>({});
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (data?.preferences) setPrefs(data.preferences);
  }, [data]);

  const modelGroups = useMemo(() => {
    if (!models.data) return [];
    const byFamily: Record<string, typeof models.data.models> = {};
    for (const m of models.data.models) (byFamily[m.family] = byFamily[m.family] ?? []).push(m);
    return FAMILY_ORDER.filter((f) => byFamily[f]?.length).map((id) => ({
      id,
      label: FAMILY_LABELS[id] ?? id,
      items: (byFamily[id] ?? []).map((m) => ({
        id: m.id,
        label: m.label,
        suffix: `$${(m.credits_per_word / 1_000_000).toFixed(6)}/kelime${m.note ? ` · ${m.note}` : ''}`,
        deprecated: false,
      })),
    }));
  }, [models.data]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await save.mutateAsync(prefs);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Kaydedilemedi');
    }
  };

  const update = <K extends keyof UserPreferences>(key: K, value: UserPreferences[K]) =>
    setPrefs((prev) => ({ ...prev, [key]: value }));

  if (isPending || models.isPending) {
    return <div className="text-muted-foreground">Yükleniyor…</div>;
  }

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <Sparkles className="w-5 h-5" /> Tercihlerim
        </h1>
        <p className="text-muted-foreground text-sm">
          Varsayılan AI modeli ve içerik oluşturma tercihleri. Yeni içerik yazarken bu değerler
          otomatik gelir.
        </p>
      </div>

      <form onSubmit={onSubmit} className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Varsayılan AI Modeli</CardTitle>
            <CardDescription>
              İçerik oluştururken kullanılacak model. Aile başına gruplanmış — popüler modeller önce.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ProviderModelSelect
              groups={modelGroups}
              value={prefs.default_ai_model ?? undefined}
              onChange={(id) => update('default_ai_model', id)}
              providerLabel="Sağlayıcı"
              modelLabel="Model"
            />
            {prefs.default_ai_model && (
              <div className="mt-2 text-xs text-muted-foreground">
                Seçili: <code className="font-mono">{prefs.default_ai_model}</code>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">İçerik Varsayılanları</CardTitle>
            <CardDescription>Yeni makale oluştururken otomatik gelen değerler.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Dil</Label>
                <select
                  value={prefs.default_language ?? ''}
                  onChange={(e) => update('default_language', e.target.value || undefined)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                >
                  <option value="">Seçme</option>
                  {LANGUAGES.map((l) => (
                    <option key={l} value={l}>
                      {l}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label>Yazı Stili / Tonu</Label>
                <select
                  value={prefs.default_tone ?? ''}
                  onChange={(e) => update('default_tone', e.target.value || undefined)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                >
                  <option value="">Seçme</option>
                  {TONES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label>Hedef Kitle</Label>
                <select
                  value={prefs.default_audience ?? ''}
                  onChange={(e) => update('default_audience', e.target.value || undefined)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                >
                  <option value="">Seçme</option>
                  {AUDIENCES.map((a) => (
                    <option key={a} value={a}>
                      {a}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label>Format</Label>
                <select
                  value={prefs.default_article_type ?? ''}
                  onChange={(e) =>
                    update('default_article_type', (e.target.value as UserPreferences['default_article_type']) || undefined)
                  }
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                >
                  <option value="">Seçme</option>
                  <option value="general">Genel makale</option>
                  <option value="how_to">Nasıl yapılır</option>
                  <option value="list">Liste yazısı</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label>Uzunluk</Label>
                <select
                  value={prefs.default_section_length ?? ''}
                  onChange={(e) =>
                    update('default_section_length', (e.target.value as UserPreferences['default_section_length']) || undefined)
                  }
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                >
                  <option value="">Seçme</option>
                  <option value="short">Kısa (~600 kelime)</option>
                  <option value="medium">Orta (~1000 kelime)</option>
                  <option value="long">Uzun (~1800 kelime)</option>
                </select>
              </div>
            </div>
          </CardContent>
        </Card>

        {error && (
          <div className="text-sm text-destructive bg-destructive/10 rounded-md border border-destructive/30 px-3 py-2">
            {error}
          </div>
        )}
        {saved && (
          <div
            className={cn(
              'text-sm text-emerald-700 dark:text-emerald-400 bg-emerald-500/10 rounded-md px-3 py-2 flex items-center gap-2',
            )}
          >
            <CheckCircle2 className="w-4 h-4" /> Kaydedildi
          </div>
        )}

        <Button type="submit" disabled={save.isPending}>
          {save.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Kaydet
        </Button>
      </form>
    </div>
  );
}
